import { useCallback, useState } from "react";

/** Client ids whose group is folded on the agency Tasks tab. Only folded
 * clients are stored, so a new client always starts open. */
type CollapsedClients = Record<string, true>;

const STORAGE_KEY = "agency-tasks:collapsed-clients";

export function readCollapsedClients(): CollapsedClients {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as CollapsedClients) : {};
  } catch {
    return {};
  }
}

export function writeCollapsedClients(state: CollapsedClients): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage full/unavailable — collapse state is a nicety, not data
  }
}

export function useCollapsedClients(): {
  isCollapsed: (clientId: string) => boolean;
  toggle: (clientId: string) => void;
} {
  const [collapsed, setCollapsed] = useState<CollapsedClients>(readCollapsedClients);

  const toggle = useCallback((clientId: string) => {
    setCollapsed((prev) => {
      const next = { ...prev };
      if (next[clientId]) delete next[clientId];
      else next[clientId] = true;
      writeCollapsedClients(next);
      return next;
    });
  }, []);

  const isCollapsed = useCallback((clientId: string) => Boolean(collapsed[clientId]), [collapsed]);

  return { isCollapsed, toggle };
}
